import { IPaginatedResponse, IPaginateParams } from "#src/@types/api";
import { FAKE_DLP } from "./fakeData";
import {
  IDLPDrive,
  IDLPActivityLog,
  IDLPSnapshot,
  IDLPSchedule,
  IBackupSettingResponse,
  IBackupStatisticsResponse,
  ISensitivityChangeRequest,
  IDataLabelSensitivityItem,
  IPeripheralPolicyItem,
} from "./types";
import endpoint from "#src/config/endpoint";
import request from "#src/utils/request";

// TODO: remove later
const IS_FAKE = !!process.env.REACT_APP_IS_FAKE;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** @deprecated */
async function listDLPDrives(
  workspaceId: string,
  params: IPaginateParams & {
    backup_enabled: string | undefined;
    sort: string | undefined;
  }
): Promise<IPaginatedResponse<IDLPDrive>> {
  if (IS_FAKE) {
    console.log("calling listDLPDrives", workspaceId, params);
    const res: IPaginatedResponse<IDLPDrive> = {
      count: FAKE_DLP.drives.length,
      results: FAKE_DLP.drives,
    };
    await delay(1000);
    return res;
  }

  const res = await request.get<never, IPaginatedResponse<IDLPDrive>>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives`,
    { params }
  );
  return res;
}

/** @deprecated */
async function listDLPLog(
  workspaceId: string,
  params: IPaginateParams & {
    sort: string | undefined;
    type: string | undefined;
    status: string | undefined;
    from: number | undefined;
    to: number | undefined;
  }
): Promise<IPaginatedResponse<IDLPActivityLog>> {
  if (IS_FAKE) {
    console.log("calling listDLPLog", workspaceId, params);
    const res: IPaginatedResponse<IDLPActivityLog> = {
      count: FAKE_DLP.logs.length,
      results: FAKE_DLP.logs,
    };
    await delay(1000);
    return res;
  }

  const res = await request.get<never, IPaginatedResponse<IDLPActivityLog>>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/activities`,
    { params }
  );
  return res;
}

/** @deprecated */
async function getDLPSchedule(
  workspaceId: string,
  driveId: number
): Promise<IDLPSchedule> {
  if (IS_FAKE) {
    console.log("calling getDLPSchedule", workspaceId, driveId);
    const res: IDLPSchedule = FAKE_DLP.schedules[0];
    await delay(1000);
    return res;
  }

  const res = await request.get<never, IDLPSchedule>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/${driveId}/schedule`
  );
  return res;
}

/** @deprecated */
async function listDLPSnapshot(
  workspaceId: string,
  driveId: number,
  params: IPaginateParams
): Promise<IPaginatedResponse<IDLPSnapshot>> {
  if (IS_FAKE) {
    console.log("calling listDLPSnapshot", workspaceId, driveId, params);
    const res: IPaginatedResponse<IDLPSnapshot> = {
      count: FAKE_DLP.snapshots.length,
      results: FAKE_DLP.snapshots,
    };
    await delay(1000);
    return res;
  }

  const res = await request.get<never, IPaginatedResponse<IDLPSnapshot>>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/${driveId}/snapshots`
  );
  return res;
}

/** @deprecated */
async function deleteDLPSnapshot(
  workspaceId: string,
  driveId: number,
  snapshotId: string
): Promise<undefined> {
  if (IS_FAKE) {
    console.log("calling deleteDLPSnapshot", workspaceId, driveId, snapshotId);
    await delay(1000);
    return;
  }

  await request.delete<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/${driveId}/snapshots/${snapshotId}`
  );
  return;
}

/** @deprecated */
async function updateDLPSchedule(
  workspaceId: string,
  driveId: number,
  data: IDLPSchedule
): Promise<{ success: boolean }> {
  if (IS_FAKE) {
    console.log("calling updateDLPSchedule", workspaceId, driveId, data);
    await delay(1000);
    return { success: true };
  }

  await request.put<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/${driveId}/schedule`,
    data
  );
  return { success: true };
}

/** @deprecated */
async function bulkUpdateDLPSchedule(
  workspaceId: string,
  driveIds: number[],
  data: IDLPSchedule
): Promise<{ success: boolean }> {
  if (IS_FAKE) {
    console.log("calling bulkUpdateDLPSchedule", workspaceId, driveIds, data);
    await delay(1000);
    return { success: true };
  }

  await request.post<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/schedules`,
    { ...data, asset_ids: driveIds }
  );
  return { success: true };
}

/** @deprecated */
async function removeDLPSchedule(
  workspaceId: string,
  driveId: number
): Promise<{ success: boolean }> {
  if (IS_FAKE) {
    console.log("calling removeDLPSchedule", workspaceId, driveId);
    await delay(1000);
    return { success: true };
  }

  await request.delete<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/${driveId}/schedule`
  );
  return { success: true };
}

/** @deprecated */
async function bulkRemoveDLPSchedule(
  workspaceId: string,
  driveIds: number[]
): Promise<{ success: boolean }> {
  if (IS_FAKE) {
    console.log("calling bulkRemoveDLPSchedule", workspaceId, driveIds);
    await delay(1000);
    return { success: true };
  }

  await request.post<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/schedules/stop`,
    { asset_ids: driveIds }
  );
  return { success: true };
}

/** @deprecated */
async function restoreDLPSnapshot(
  workspaceId: string,
  driveId: number,
  snapshotId: string
): Promise<{ success: boolean }> {
  if (IS_FAKE) {
    console.log("calling restoreDLPSnapshot", workspaceId, driveId, snapshotId);
    await delay(1000);
    return { success: true };
  }

  await request.post<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/dlp/drives/${driveId}/snapshots/${snapshotId}`
  );
  return { success: true };
}

async function retrieveBackupStatistics(workspaceId: string) {
  return await request.get<never, IBackupStatisticsResponse>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/statistic`
  );
}

async function retrieveBackupSettings(workspaceId: string) {
  return await request.get<never, IBackupSettingResponse>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/setting`
  );
}

async function updateBackupSettings(
  workspaceId: string,
  settings: IBackupSettingResponse
) {
  return await request.put<never, { success: boolean; error?: string }>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/setting`,
    settings
  );
}

async function listDataLabelSensitivity(workspaceId: string) {
  return await request.get<never, IDataLabelSensitivityItem[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/sensitivities`
  );
}

async function listDataLabelContent(workspaceId: string) {
  return await request.get<never, { id: number; name: string }[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/contents`,
    { params: { paging: "0" } }
  );
}

async function createDataLabelContent(workspaceId: string, tagNames: string[]) {
  return await request.post<never, { id: string }>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/contents`,
    tagNames.map((item) => ({ name: item }))
  );
}

async function deleteDataLabelContent(workspaceId: string, tagIds: string[]) {
  return await request.post<never, { id: string }>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/contents/delete_multiple`,
    { ids: tagIds }
  );
}

async function listSensitivityChangeRequest(
  workspaceId: string,
  params: {
    // Status can be "rejected" | "approved"
    status: string | undefined;
    sort: string | undefined;
  }
) {
  return await request.get<never, ISensitivityChangeRequest[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/sensitivity_change_requests`,
    { params: { ...params, paging: "0" } }
  );
}

async function updateSensitivityChangeRequest(
  workspaceId: string,
  requestId: string,
  status: string
) {
  return await request.put<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/sensitivity_change_requests/${requestId}`,
    { status }
  );
}

async function listPeripheralPolicies(workspaceId: string) {
  return await request.get<never, IPaginatedResponse<IPeripheralPolicyItem>>(
    `${endpoint.WORKSPACES}/${workspaceId}/peripheral_policies`
  );
}

async function updatePeripheralPolicies(
  workspaceId: string,
  policyId: string,
  policyDetails: {
    sources: { id: string; type: string }[];
    exclude: { id: string; type: string }[];
    block_type: string;
    enabled: boolean;
    all_hosts: boolean;
  }
) {
  return await request.put<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/peripheral_policies/${policyId}`,
    policyDetails
  );
}

async function retrieveConfigSensitivityRequest(workspaceId: string) {
  return await request.get<never, { enable: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/config/sensitivity_request`
  );
}

async function updateConfigSensitivityRequest(
  workspaceId: string,
  enable: boolean
) {
  return await request.put<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/backup/data_labels/config/sensitivity_request`,
    { enable }
  );
}

const dlpService = {
  listDLPDrives,
  listDLPLog,
  listDLPSnapshot,
  getDLPSchedule,
  updateDLPSchedule,
  bulkUpdateDLPSchedule,
  removeDLPSchedule,
  bulkRemoveDLPSchedule,
  restoreDLPSnapshot,
  deleteDLPSnapshot,

  retrieveBackupStatistics,
  retrieveBackupSettings,
  updateBackupSettings,

  listDataLabelSensitivity,
  listDataLabelContent,
  createDataLabelContent,
  deleteDataLabelContent,
  listSensitivityChangeRequest,
  updateSensitivityChangeRequest,

  listPeripheralPolicies,
  updatePeripheralPolicies,
  retrieveConfigSensitivityRequest,
  updateConfigSensitivityRequest,
};

export default dlpService;
